import { pageElements, renderPage } from "./element-signals.js";
import { pageData } from "../view-models/page-signals.js";
import { Customised } from "./custom-element.js";
import {
  pdfDocument,
  pdfLoadingError,
  pdfLoadingComplete,
  pdfLoadingProgress,
  pdfReady,
} from "../view-models/book-signals.js";
import { html } from "uhtml";
import { locale } from "../view-models/app-signals.js";
import { locales } from "../models/locales.js";

function reload() {
  window.location.reload();
}

export class PDFDocumentElement extends Customised(HTMLElement) {
  static tagName = "cassis-pdf";
  loaded = false;
  progress = 0;
  spread = false;
  signals() {
    return {
      loadingPromise: pdfLoadingComplete(),
      data: pageData(),
      progress: pdfLoadingProgress().percent,
      ready: pdfReady(),
      error: pdfLoadingError(),
      locale: locale(),
    };
  }
  render({ data, progress, ready, loadingPromise, error, locale }) {
    this.loadingPromise = loadingPromise;
    if (error) {
      // Without this the loading bar stayed up for ever when a PDF could
      // not be fetched or parsed.
      this.removeAttribute("aria-busy");
      this.setAttribute("error", "");
      this.setAttribute("loaded", "false");
      return html`<div class="cassis-pdf-error" role="alert">
        <p>${locales.errorLabel[locale] || locales.errorLabel.en}</p>
        <button type="button" @click=${reload}>
          ${locales.tryAgain[locale] || locales.tryAgain.en}
        </button>
      </div>`;
    }
    this.removeAttribute("error");
    this.data = data;
    this.spread = data.spread;
    this.progress = progress;
    if (this.data?.ratio) {
      this.setAttribute("style", `--total-scale-factor: ${this.data.ratio};`);
    }
    if (progress === 100) {
      this.loaded = true;
    }
    if (ready) {
      this.progress = 100;
      this.loaded = true;
      this.removeAttribute("aria-busy");
    }
    if (this.loaded) {
      document.documentElement.classList.add("cassis-pdf-loaded");
    }
    this.setAttribute("spread", this.spread);
    this.setAttribute("progress", this.progress);
    this.setAttribute("loaded", this.loaded);
    return html` <cassis-loading
        loaded="${this.loaded}"
        progress="${this.progress}"
      ></cassis-loading>
      <cassis-pdf-page
        id="start"
        position="start"
        page="${this.data.pages[0]}"
      ></cassis-pdf-page>
      <cassis-pdf-page
        id="end"
        position="end"
        ?hidden="${!this.data.pages[1]}"
        page="${this.data.pages[1]}"
      ></cassis-pdf-page>`;
  }
  firstUpdated() {
    pageElements.root(this);
    pageElements.start(this.querySelector("#start"));
    pageElements.end(this.querySelector("#end"));
    if (!this.hasAttribute("error")) {
      this.setAttribute("aria-busy", "true");
    }
    this.setAttribute("aria-describedby", "cassis-loading-progress");
  }
}
PDFDocumentElement.define();

export class PDFPageElement extends Customised(HTMLElement) {
  static tagName = "cassis-pdf-page";
  rendered = false;
  // The page number whose paint is currently on the canvas, or null.
  renderedPage = null;
  get page() {
    return Number.parseInt(this.getAttribute("page"), 10);
  }
  signals() {
    // The document is a dependency so that loading a different PDF of the
    // same page size still repaints.
    return { data: pageData(), pdf: pdfDocument() };
  }
  updated() {
    // renderComplete() (element-signals.js) is how callers wait for this.
    renderPage(this, this.page, this.id === "start");
  }
  render({ data }) {
    // console.log("rendering cassis-pdf-page");
    this.data = data;
    // console.log("element data before render: ", data);
    this.position = this.getAttribute("position");
    const width = Math.floor(data.width);
    const height = Math.floor(data.height);
    const scale = data.outputScale;
    this.ratio = this.data.ratio;
    const scaledHeight = height * scale;
    const scaledWidth = width * scale;
    const mainStyle = `width: ${width}px; height: ${height}px;`;
    this.setAttribute("style", mainStyle);
    const style = `position: absolute; top: 0; left: 0; width: ${width}px; height: ${height}px;`;
    const canvasStyle = `${style} z-index: 0;`;
    const textStyle = `${style} z-index: 1;`;
    return html`<canvas
        class="cassis-pdf-canvas-layer"
        width="${scaledWidth}"
        height="${scaledHeight}"
        style="${canvasStyle}"
      ></canvas>
      <div class="cassis-pdf-text-layer" style="${textStyle}"></div>`;
  }
}
PDFPageElement.define();
